import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { login as apiLogin } from '../api/client';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

// Bridge-callback failures redirect here as /?bridgeError=<code> — map to
// Lithuanian user-facing text (the client-facing login surface is LT).
const BRIDGE_ERRORS: Record<string, string> = {
  invalid_request: 'Prisijungimo užklausa netinkama. Bandykite dar kartą.',
  state_mismatch: 'Prisijungimo sesija baigėsi — bandykite prisijungti iš naujo.',
  exchange_failed: 'Nepavyko susisiekti su integravizija.lt. Bandykite vėliau.',
  invalid_code: 'Prisijungimo kodas pasenęs arba jau panaudotas — bandykite dar kartą.',
  hosting_required: 'a5suite prieinama tik hostingo paslaugą turintiems klientams. Susisiekite per integravizija.lt.',
  a5suite_disabled: 'a5suite prieiga šiai paskyrai dar neįjungta. Susisiekite per integravizija.lt.',
};

export default function Login({ onLogin }: { onLogin: () => void }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  // Bridge-callback failures arrive as /?bridgeError=<code> — seed the error
  // state from the URL once, at mount.
  const [error, setError] = useState(() => {
    const code = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('bridgeError')
      : null;
    return code ? (BRIDGE_ERRORS[code] ?? 'Prisijungti nepavyko. Bandykite dar kartą.') : '';
  });
  const [loading, setLoading] = useState(false);
  const { bridgeLoginEnabled, localLoginEnabled, loading: flagsLoading } = useFeatureFlags();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await apiLogin({ username, password });
      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const startBridgeLogin = () => {
    window.location.href = '/api/auth/bridge/begin';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm p-8 bg-white rounded-lg shadow-md">
        <h1 className="text-center mb-6">
          <img src="/logo.png" alt="A5Suite" className="h-10 mx-auto" />
        </h1>

        {error && (
          <div className="p-3 mb-4 text-sm text-red-700 bg-red-50 rounded-md">{error}</div>
        )}

        {/* Client-facing auth: Integravizija SSO (Google login on the shop side,
            no self-registration). */}
        {flagsLoading ? (
          <div className="text-center text-sm text-gray-400 py-4">…</div>
        ) : (
          <>
            {bridgeLoginEnabled && (
              <button
                type="button"
                onClick={startBridgeLogin}
                className="w-full py-2 px-4 mb-4 text-white bg-primary-600 rounded-md hover:bg-primary-700 font-medium"
              >
                Prisijungti per integravizija.lt
              </button>
            )}
            {!bridgeLoginEnabled && !localLoginEnabled && (
              <div className="p-3 text-sm text-gray-600 bg-gray-50 rounded-md">
                Prisijungimas šiuo metu neprieinamas.
              </div>
            )}
          </>
        )}

        {/* Local username/password — operator/dev only (Auth:EnableLocalLogin). */}
        {localLoginEnabled && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.username')}
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 font-medium"
            >
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </button>
          </form>
        )}

        <div className="mt-4 text-center text-xs text-gray-400">
          <a href="/privatumas" className="hover:text-gray-600">Privatumo pranešimas</a>
          <span className="mx-1">·</span>
          <a href="/taisykles" className="hover:text-gray-600">Taisyklės</a>
        </div>
      </div>
    </div>
  );
}
